/**
 * Export feasibility — a pre-flight check the dialog runs BEFORE the user clicks
 * Download, so an impossible export shows an actionable error up front instead
 * of the generic "Export failed" toast at the very end.
 *
 * The failure it predicts: some export modes render the WHOLE document to ONE
 * canvas, and if a dimension of that canvas exceeds the browser's max canvas
 * size, `canvas.toBlob` returns null (or the draw silently produces nothing) and
 * the export fails. The single-canvas modes are:
 *   - PNG / JPEG        — always one image of the full content.
 *   - PDF, page "auto"  — Fit-Content renders the whole doc onto one page.
 *   - PDF, fit-to-page  — the whole doc is scaled onto one page.
 * Multi-page PDF is paged (every page canvas is bounded), so it is always
 * feasible no matter how tall the document is.
 *
 * All the inputs (capture width, content height, the page geometry that sets the
 * render scale) are known once the capture settles, so the outcome is
 * predictable without attempting the (expensive) render.
 *
 * Kept in its own module (no jsPDF/snapdom imports) so it's unit-testable, and
 * so both the dialog and htmlToPdf share the exact same scale math + predicate.
 * (export-capture is import-safe here: the constants it exports are pure data.)
 */
import { PAGE_DIMS } from './export-capture.js'

/** Render scale clamp — shared with htmlToPdf's page/image scaling. */
export const MAX_RENDER_SCALE = 5
/** Images have no physical page — target a fixed minimum output width (~2.5k). */
const IMAGE_TARGET_PX = 2560
const TARGET_DPI = 300
const MM_PER_INCH = 25.4

/** CSS px → device px for image exports. Scale rises as capture width shrinks so
 *  output resolution stays ~constant regardless of layout width. */
export function imageRenderScale(captureWidth: number): number {
  return Math.min(MAX_RENDER_SCALE, Math.max(2, IMAGE_TARGET_PX / captureWidth))
}

/** CSS px → device px for a PDF page, targeting ~300 DPI across the content box
 *  width (mm). Shared with htmlToPdf so the feasibility scale matches the render. */
export function pageRenderScale(captureWidth: number, boxWmm: number): number {
  const targetPx = (TARGET_DPI * boxWmm) / MM_PER_INCH
  return Math.min(MAX_RENDER_SCALE, Math.max(2, targetPx / captureWidth))
}

// The max usable canvas dimension is browser-specific (Chrome ~65535, Firefox
// 32767, WebKit lower), so probe the real limit once at runtime rather than
// hardcoding a conservative value that would wrongly block exports the current
// browser handles fine. The probe uses 1px-wide canvases, so it costs a few KB
// even at 65k. Cached for the session.
// Canvas AREA caps are separate from the per-dimension cap and the dim×1 probe
// cannot see them (its probe canvases are always ≤64KB of pixels). Measured
// caps (canvas-size test data): Chrome/desktop-Safari ~268M px (16384²),
// Firefox ~125M px (11180²), older iOS ~16.7M px (4096²). Probing the real
// area cap means committing a multi-hundred-MB backing store, so use known
// conservative constants instead, picked off the probed per-dimension cap
// (Firefox probes ~32767; Chrome/Safari ~65535). Exceeding the area cap makes
// the draw silently blank / toBlob return null — the exact post-hoc failure
// this module exists to preempt.
const IS_IOS = typeof navigator !== 'undefined'
  && (/iP(hone|ad|od)/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
export function maxCanvasArea(): number {
  if (IS_IOS) return 16_777_216
  return maxCanvasDim() <= 33_000 ? 124_000_000 : 268_000_000
}

/** Pure predicate (unit-testable without a real browser's caps). */
export function exceedsCanvasLimits(outputW: number, outputH: number, maxDim: number, maxArea: number): boolean {
  return outputW > maxDim || outputH > maxDim || outputW * outputH > maxArea
}

let cachedMaxCanvasDim = 0
export function maxCanvasDim(): number {
  if (cachedMaxCanvasDim) return cachedMaxCanvasDim
  if (typeof document === 'undefined') return 8192
  const usable = (dim: number): boolean => {
    try {
      const c = document.createElement('canvas')
      c.width = dim
      c.height = 1
      const cctx = c.getContext('2d')
      if (!cctx) return false
      cctx.fillStyle = '#fff'
      cctx.fillRect(dim - 1, 0, 1, 1)
      const painted = cctx.getImageData(dim - 1, 0, 1, 1).data[3] !== 0
      c.width = c.height = 0
      return painted
    } catch {
      return false
    }
  }
  let lo = 4096
  let hi = 65536
  if (!usable(lo)) return (cachedMaxCanvasDim = lo)
  while (hi - lo > 256) {
    const mid = (lo + hi) >> 1
    if (usable(mid)) lo = mid
    else hi = mid
  }
  return (cachedMaxCanvasDim = lo)
}

/** The subset of export options that actually influence whether the export can
 *  be produced (the single-canvas render dimensions). Everything else — colors,
 *  quality, header/footer, page range — is irrelevant to feasibility. */
export interface FeasibilityInput {
  format: 'pdf' | 'png' | 'jpeg' | undefined
  pageSize?: 'auto' | 'a4' | 'letter' | 'legal'
  orientation?: 'portrait' | 'landscape'
  fitToPage?: boolean
  marginMm?: number
  captureWidth: number
  contentHeight: number
}

export interface ExportFeasibility {
  ok: boolean
  reason?: 'canvas-limit'
  outputW?: number
  outputH?: number
  maxDim?: number
}

/** The content box width (mm) a single-page PDF renders across — drives the
 *  render scale, exactly as in htmlToPdf's auto / fit-to-page branch. */
function singlePagePdfBoxWidthMm(opts: FeasibilityInput): number {
  // Fit-Content ("auto") ignores margins entirely (the page IS the content —
  // htmlToPdf zeroes marginMm for auto; keep the scale math in lockstep) and
  // renders portrait at A4 width; fixed sizes honor orientation.
  const margin = opts.pageSize === 'auto' ? 0 : Math.max(0, opts.marginMm ?? 0)
  const pageWmm =
    opts.pageSize === 'auto'
      ? PAGE_DIMS.a4[0]
      : (PAGE_DIMS[opts.pageSize ?? 'a4'] ?? PAGE_DIMS.a4)[opts.orientation === 'landscape' ? 1 : 0]
  return Math.max(1, pageWmm - 2 * margin)
}

/**
 * Predict whether the current options can actually be exported in this browser.
 * Only the single-canvas modes (image formats; PDF with page "auto" or
 * fit-to-page) can exceed the browser's canvas dimension cap — multi-page PDF is
 * always feasible. Returns `ok: true` while dimensions aren't known yet
 * (still settling).
 */
export function checkExportFeasibility(opts: FeasibilityInput): ExportFeasibility {
  const { format, captureWidth, contentHeight } = opts
  if (!captureWidth || !contentHeight) return { ok: true }

  const isImage = format === 'png' || format === 'jpeg'
  const isSinglePagePdf =
    (format ?? 'pdf') === 'pdf' && (opts.pageSize === 'auto' || opts.fitToPage === true)
  if (!isImage && !isSinglePagePdf) return { ok: true }

  const scale = isImage
    ? imageRenderScale(captureWidth)
    : pageRenderScale(captureWidth, singlePagePdfBoxWidthMm(opts))
  const outputW = Math.round(captureWidth * scale)
  const outputH = Math.round(contentHeight * scale)
  const maxDim = maxCanvasDim()
  if (exceedsCanvasLimits(outputW, outputH, maxDim, maxCanvasArea())) {
    return { ok: false, reason: 'canvas-limit', outputW, outputH, maxDim }
  }
  return { ok: true }
}
